/**
 * 결제 화면 — 서비스에서 넘어와 콘솔에서 결제한다 (#244)
 *
 *   서비스 → /pay/{order_id} → (로그인) → 주문 확인 → 토스 결제창 → /pay/{order_id}/done | /fail → 서비스
 *
 * 금액은 서버가 주문에 저장한 것을 보여 주고 그대로 결제창에 넘긴다. 이 화면이 금액을 정하지 않는다.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { TossPaymentsCheckout } from '@/components/billing/payment/TossPaymentsCheckout';
import { GatewayCard, GatewaySpinner } from '@/components/billing/gateway/GatewayCard';
import { useRequireLogin } from '@/hooks/auth/useRequireLogin';
import { CheckoutOrder, paymentOrderAPI } from '@/services/apis/paymentOrderAPI';
import { getErrorMessage } from '@/utils/errorUtils';
import { formatMoney } from '@/utils/moneyUtils';

const FINISHED: Record<string, string> = {
  paid: '이미 결제가 완료된 요청입니다.',
  failed: '결제가 취소되었거나 실패한 요청입니다. 서비스에서 결제를 다시 시작해 주세요.',
  expired: '결제 가능 시간(30분)이 지났습니다. 서비스에서 결제를 다시 시작해 주세요.',
};

const PaymentOrderPage: React.FC = () => {
  const { orderId = '' } = useParams();
  const loggedIn = useRequireLogin();
  const [order, setOrder] = useState<CheckoutOrder | null>(null);
  const [error, setError] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!loggedIn) return;
    paymentOrderAPI.get(orderId)
      .then(setOrder)
      .catch((e) => setError(getErrorMessage(e, '결제 요청을 불러오지 못했습니다.')));
  }, [loggedIn, orderId]);

  const cancel = async () => {
    setCancelling(true);
    try {
      const { redirect_to } = await paymentOrderAPI.fail(orderId, 'USER_CANCEL', '사용자가 결제 전에 취소함');
      window.location.replace(redirect_to);
    } catch (e) {
      setError(getErrorMessage(e, '취소를 처리하지 못했습니다.'));
      setCancelling(false);
    }
  };

  if (error) {
    return (
      <GatewayCard title="결제를 진행할 수 없습니다">
        <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>
      </GatewayCard>
    );
  }
  if (!order) {
    return <GatewayCard title="결제"><GatewaySpinner message="결제 요청을 불러오는 중입니다." /></GatewayCard>;
  }
  if (order.status !== 'pending' || !order.toss_client_key) {
    return (
      <GatewayCard title="결제">
        <p className="mb-6 text-sm text-gray-700">{FINISHED[order.status] ?? '결제할 수 없는 요청입니다.'}</p>
        <Button className="w-full" onClick={() => window.location.replace(order.return_url)}>서비스로 돌아가기</Button>
      </GatewayCard>
    );
  }

  const origin = window.location.origin;
  return (
    <GatewayCard title="결제">
      {order.is_test_mode && (
        <p className="mb-4 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">테스트 결제입니다. 실제로 청구되지 않습니다.</p>
      )}
      <dl className="mb-6 space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-gray-500">서비스</dt><dd>{order.service_name}</dd></div>
        <div className="flex justify-between"><dt className="text-gray-500">상품</dt><dd className="text-right">{order.order_name}</dd></div>
        <div className="flex justify-between border-t pt-2 text-base font-semibold">
          <dt>결제 금액</dt><dd>{formatMoney(order.amount, order.currency)}</dd>
        </div>
      </dl>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={cancel} disabled={cancelling}>
          {cancelling ? '취소하는 중' : '취소'}
        </Button>
        <Button className="flex-1" onClick={() => setCheckoutOpen(true)} disabled={cancelling}>결제하기</Button>
      </div>
      {checkoutOpen && (
        <TossPaymentsCheckout
          clientKey={order.toss_client_key}
          amount={order.amount}
          orderId={order.order_id}
          orderName={order.order_name}
          successUrl={`${origin}/pay/${order.order_id}/done`}
          failUrl={`${origin}/pay/${order.order_id}/fail`}
          onClose={() => setCheckoutOpen(false)}
        />
      )}
    </GatewayCard>
  );
};

export default PaymentOrderPage;
